import React, { useState, useEffect } from 'react';
import { supabase, supabaseConfigMeta } from '../../lib/supabaseClient';
import { getApiBaseUrl } from '../../lib/apiBase';

const API_BASE_URL = getApiBaseUrl();

const formatAuthError = (message = '') => {
  const text = String(message || '');
  if (/email rate limit exceeded/i.test(text)) {
    return 'Invite email limit reached. Please wait and retry later, or create user with temporary password.';
  }
  if (/invalid value/i.test(text) && /fetch/i.test(text)) {
    return 'Supabase URL is invalid in deployment settings. Verify REACT_APP_SUPABASE_URL and redeploy.';
  }
  return text;
};

function AdminPortal({ onJobPosted, editingJob }) {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authDebug, setAuthDebug] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [postedJob, setPostedJob] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // States for setting up password after clicking email invite
  const [newPassword, setNewPassword] = useState('');
  const [hidePasswordPrompt, setHidePasswordPrompt] = useState(false);

  const [formData, setFormData] = useState({
    title: '', department: '', location: '', type: 'Full-Time', description: '', requirements: '',
    salary: '', experience: '', tags: '', jobCode: '', workModel: 'Onsite',
    responsibilities: '', skills: '', manager: '', workAuth: '', experience_level: 'Mid-Level'
  });

  // Milestones management states
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'milestones'
  const [milestones, setMilestones] = useState([]);
  const [milestoneForm, setMilestoneForm] = useState({ year: '', title: '', description: '' });
  const [isSubmittingMilestone, setIsSubmittingMilestone] = useState(false);
  const [userInviteEmail, setUserInviteEmail] = useState('');
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);

  const [portalToast, setPortalToast] = useState({ message: '', type: 'info' });
  const showToast = (message, type = 'info') => {
    setPortalToast({ message, type });
    setTimeout(() => setPortalToast({ message: '', type: 'info' }), 4000);
  };

  useEffect(() => {
    let mounted = true;

    if (editingJob) {
      setFormData({
        ...editingJob,
        requirements: '' // Quick workaround as original desc might contain reqs
      });
    }

    // Check initial auth state
    if (supabase) {
      // Handle Supabase invite/recovery callbacks before checking session.
      // Supabase can send either:
      // 1) query params: ?token_hash=...&type=invite
      // 2) hash params: #access_token=...&refresh_token=...&type=invite
      const url = new URL(window.location.href);
      const tokenHash = url.searchParams.get('token_hash');
      const type = url.searchParams.get('type');
      const code = url.searchParams.get('code');
      const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const hashType = hashParams.get('type');

      const initAuth = async () => {
        try {
          if (tokenHash && (type === 'invite' || type === 'recovery')) {
            const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
              if (error) {
                if (mounted) {
                  setAuthDebug({
                    stage: 'verifyOtp',
                    message: error.message || null,
                    status: error.status || null,
                    code: error.code || null,
                    name: error.name || null,
                  });
                }
                if (mounted) setAuthError(formatAuthError(error.message || 'Invite verification failed. Please request a new invite.'));
            } else {
              window.history.replaceState({}, document.title, '/admin');
            }
          } else if (accessToken && refreshToken && (hashType === 'invite' || hashType === 'recovery')) {
            const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
              if (error) {
                if (mounted) {
                  setAuthDebug({
                    stage: 'setSession',
                    message: error.message || null,
                    status: error.status || null,
                    code: error.code || null,
                    name: error.name || null,
                  });
                }
                if (mounted) setAuthError(formatAuthError(error.message || 'Invite session setup failed. Please request a new invite.'));
            } else {
              window.history.replaceState({}, document.title, '/admin');
            }
          } else if (code) {
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              if (mounted) {
                setAuthDebug({
                  stage: 'exchangeCode',
                  message: error.message || null,
                  status: error.status || null,
                  code: error.code || null,
                  name: error.name || null,
                });
              }
              if (mounted) setAuthError(formatAuthError(error.message || 'Invite code exchange failed.'));
            } else {
              window.history.replaceState({}, document.title, '/admin');
            }
          }

          const { data: { session } } = await supabase.auth.getSession();
          if (!mounted) return;
          setSession(session);
          if (session) {
            const isHidden = localStorage.getItem(`hide_pwd_${session.user.id}`) === 'true' || session.user.user_metadata?.has_set_password === true;
            setHidePasswordPrompt(isHidden);
          }
        } catch (error) {
          if (mounted) {
            setAuthDebug({
              stage: 'initAuth',
              message: error?.message || null,
              status: error?.status || null,
              code: error?.code || null,
              name: error?.name || null,
            });
            setAuthError(formatAuthError(error?.message || 'Authentication initialization failed. Please check Supabase URL settings.'));
          }
        } finally {
          if (mounted) setIsLoading(false);
        }
      };
      initAuth();

      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        setSession(session);
        if (session) {
          const isHidden = localStorage.getItem(`hide_pwd_${session.user.id}`) === 'true' || session.user.user_metadata?.has_set_password === true;
          setHidePasswordPrompt(isHidden);
        }
      });

      return () => {
        mounted = false;
        subscription.unsubscribe();
      };
    } else {
      setIsLoading(false);
    }
  }, [editingJob]);

  // Standard Login with Email and Password
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthDebug(null);
    if (!supabase) return setAuthError('Supabase is not configured.');

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthDebug({
          stage: 'signInWithPassword',
          message: error.message || null,
          status: error.status || null,
          code: error.code || null,
          name: error.name || null,
          config: supabaseConfigMeta,
        });
        setAuthError(formatAuthError(error.message));
      }
    } catch (error) {
      setAuthDebug({
        stage: 'signInCatch',
        message: error?.message || null,
        status: error?.status || null,
        code: error?.code || null,
        name: error?.name || null,
        config: supabaseConfigMeta,
      });
      setAuthError(formatAuthError(error?.message || 'Login failed due to invalid Supabase URL/settings.'));
    }
  };

  // Set New Password (Used right after clicking the email invite)
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!supabase) return;

    // Since they clicked an invite link, they are already logged in in the background!
    // We just tell Supabase to give this account a permanent password
    // AND we set a permanent flag in their profile metadata so this prompt NEVER shows again on any device!
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      data: { has_set_password: true }
    });
    if (error) {
      setAuthError(error.message);
      showToast('Error: ' + error.message, 'error');
    } else {
      showToast("Password saved securely! You can now log into your dashboard anytime using this password.", 'success');
      setNewPassword('');
      setHidePasswordPrompt(true);
      if (session) {
        localStorage.setItem(`hide_pwd_${session.user.id}`, 'true');
      }
    }
  };

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supabase) {
      showToast("Supabase not configured", 'error');
      return;
    }

    try {
      let response;
      const jobPayload = {
        title: formData.title,
        department: formData.department,
        location: formData.location,
        type: formData.type,
        status: 'active',
        description: formData.description + (formData.requirements ? `\n\nRequirements:\n${formData.requirements}` : ''),
        author_id: session.user.id,
        salary: formData.salary,
        experience: formData.experience,
        tags: formData.tags,
        job_code: formData.jobCode,
        work_model: formData.workModel,
        responsibilities: formData.responsibilities,
        skills: formData.skills,
        manager: formData.manager,
        work_auth: formData.workAuth,
        experience_level: formData.experience_level
      };

      if (editingJob && editingJob.id) {
        response = await supabase.from('jobs').update(jobPayload).eq('id', editingJob.id).select();
      } else {
        response = await supabase.from('jobs').insert([jobPayload]).select();
      }

      if (response.error) throw response.error;

      const savedJob = response.data[0];
      onJobPosted(savedJob);
      setFormData({
        title: '', department: '', location: '', type: 'Full-Time', description: '', requirements: '',
        salary: '', experience: '', tags: '', jobCode: '', workModel: 'Onsite',
        responsibilities: '', skills: '', manager: '', workAuth: '', experience_level: 'Mid-Level'
      });
      if (!editingJob) {
        setPostedJob(savedJob);
        setLinkCopied(false);
      }
      showToast(editingJob ? 'Job updated successfully!' : 'Job posted successfully!', 'success');
    } catch (err) {
      showToast('Error posting job: ' + err.message, 'error');
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/milestones`);
      const data = await res.json();
      setMilestones(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching milestones:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'milestones') {
      fetchMilestones();
    }
  }, [activeTab]);

  const handleMilestoneSubmit = async (e) => {
    e.preventDefault();
    const token = await getAccessToken();
    if (!token) {
      showToast('Session expired. Please sign in again.', 'error');
      return;
    }
    setIsSubmittingMilestone(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/milestones`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(milestoneForm)
      });
      if (res.ok) {
        setMilestoneForm({ year: '', title: '', description: '' });
        fetchMilestones();
        showToast('Milestone added successfully!', 'success');
      } else {
        const text = await res.text();
        console.error('Server response:', text);
        try {
          const err = JSON.parse(text);
          throw new Error(err.error || 'Failed to add milestone');
        } catch (e) {
          throw new Error(`Server returned non-JSON response (${res.status}): ${text.substring(0, 50)}...`);
        }
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setIsSubmittingMilestone(false);
    }
  };

  const handleMilestoneDelete = async (id) => {
    if (!window.confirm('Delete this milestone?')) return;
    const token = await getAccessToken();
    if (!token) {
      showToast('Session expired. Please sign in again.', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/milestones/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        fetchMilestones();
        showToast('Milestone deleted', 'success');
      } else {
        showToast('Failed to delete milestone', 'error');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  };

  const getAccessToken = async () => {
    if (!supabase) return '';
    const { data: { session: liveSession } } = await supabase.auth.getSession();
    const token = liveSession?.access_token || '';
    if (liveSession) setSession(liveSession);
    return token;
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    const token = await getAccessToken();
    if (!token) {
      showToast('Session expired. Please sign in again.', 'error');
      return;
    }
    setIsSubmittingInvite(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin-users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: userInviteEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to send invitation.');
      showToast(data?.message || 'Invitation sent successfully.', 'success');
      setUserInviteEmail('');
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  if (!supabase) {
    return (
      <div className="pt-32 pb-24 min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="bg-white p-12 rounded-[2.5rem] shadow-xl border border-slate-100 max-w-md w-full text-center">
          <h2 className="text-3xl font-black text-primary mb-6">Configuration Missing</h2>
          <p className="text-slate-600 font-bold mb-8">
            The Supabase environment variables are not correctly configured in your deployment settings.
          </p>
          <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-sm font-black text-left">
            Error: REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY is missing.
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) return <div className="py-20 text-center">Loading Admin Portal...</div>;

  // SCENARIO 2: They are NOT logged in and need to log in normally
  if (!session) {
    return (
      <form onSubmit={handleLogin} className="max-w-md mx-auto bg-slate-50 p-8 rounded-3xl border border-slate-200 space-y-6">
        <h3 className="text-2xl font-black text-primary text-center">Employee Login</h3>
        {authError && <p className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">{authError}</p>}
        {authDebug && (
          <details className="text-left bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px]">
            <summary className="cursor-pointer font-bold">Debug details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">{JSON.stringify(authDebug, null, 2)}</pre>
          </details>
        )}
        <p className="text-[12px] text-slate-500 font-semibold text-center">
          First-time invite users: if invite email fails due rate limit, ask admin to create your account with a temporary password.
        </p>
        <div>
          <input
            type="email"
            placeholder="Work Email"
            className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <input
            type="password"
            placeholder="Password"
            className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="w-full py-4 bg-primary text-white font-black rounded-xl hover:bg-secondary transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98]">
          Sign In
        </button>
      </form>
    );
  }

  // SCENARIO 3: They are securely logged in.
  return (
    <div className="space-y-8 relative">
      {portalToast.message && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-2xl font-bold text-sm text-white transition-all animate-fadeIn ${portalToast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
          {portalToast.message}
        </div>
      )}

      {/* Share Job Modal - shown after successful job post */}
      {postedJob && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-md w-full p-8 space-y-6 relative">
            <button
              onClick={() => setPostedJob(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-black text-primary">Job Posted Successfully!</h3>
              <p className="text-sm text-slate-500 font-medium mt-1">Share this opportunity with the world</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2 border border-slate-200">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/careers/${postedJob.id}`}
                className="flex-1 bg-transparent text-sm text-slate-700 font-medium outline-none truncate"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/careers/${postedJob.id}`);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className={`px-4 py-2 rounded-lg text-xs font-black transition-all whitespace-nowrap ${linkCopied ? 'bg-emerald-500 text-white' : 'bg-white text-primary border border-slate-200 hover:bg-slate-100'}`}
              >
                {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            <div className="space-y-3">
              <a
                href={(() => {
                  const jobLink = `${window.location.origin}/careers/${postedJob.id}`;
                  const lines = [
                    `We're hiring! ${postedJob.title} at Libsys`,
                    '',
                    postedJob.location ? `Location: ${postedJob.location}` : '',
                    postedJob.type ? `Type: ${postedJob.type}` : '',
                    postedJob.work_model ? `Work Model: ${postedJob.work_model}` : '',
                    postedJob.experience ? `Experience: ${postedJob.experience}` : '',
                    '',
                    `Apply here: ${jobLink}`,
                    '',
                    '#hiring #jobs #careers'
                  ].filter(Boolean).join('\n');
                  return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(lines)}`;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 bg-[#0A66C2] hover:bg-[#004182] text-white font-black rounded-xl transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-500/20 active:scale-[0.98]"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Share on LinkedIn
              </a>

              <button
                onClick={() => setPostedJob(null)}
                className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black rounded-xl transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Security (Show at top until password is set) */}
      {!hidePasswordPrompt && (
        <form onSubmit={handleSetPassword} className="bg-white p-8 rounded-3xl border-2 border-secondary/20 space-y-4 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
          <button
            type="button"
            onClick={() => {
              setHidePasswordPrompt(true);
              if (session) {
                localStorage.setItem(`hide_pwd_${session.user.id}`, 'true');
              }
            }}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2"
            title="Dismiss"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h4 className="text-xl font-black text-primary">Finish Setting Up Your Account</h4>
          <p className="text-sm text-slate-600 font-bold max-w-2xl">
            You are currently logged in via an invite link. To log in normally next time, please set a password for your account now.
          </p>
          
          {authError && <p className="text-red-500 text-sm font-bold bg-red-50 p-3 rounded-lg">{authError}</p>}

          <div className="flex flex-col sm:flex-row gap-4 max-w-xl pr-12">
            <input
              type="password"
              placeholder="Create New Password"
              className="flex-1 p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-slate-50 font-bold placeholder:font-normal placeholder:text-slate-300"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
            />
            <button type="submit" className="px-8 py-4 bg-secondary text-white font-black rounded-xl hover:bg-primary transition-all whitespace-nowrap active:scale-[0.98] shadow-lg shadow-blue-500/20">
              Save Password
            </button>
          </div>
        </form>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-4 p-1 bg-slate-200/50 rounded-2xl w-fit">
        <button
          onClick={() => setActiveTab('jobs')}
          className={`px-8 py-3 rounded-xl font-black text-sm transition-all ${activeTab === 'jobs' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}
        >
          Manage Jobs
        </button>
        <button
          onClick={() => setActiveTab('milestones')}
          className={`px-8 py-3 rounded-xl font-black text-sm transition-all ${activeTab === 'milestones' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}
        >
          Manage Milestones
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-8 py-3 rounded-xl font-black text-sm transition-all ${activeTab === 'users' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}
        >
          Manage Access
        </button>
      </div>

      {activeTab === 'jobs' ? (
        <form onSubmit={handleSubmit} className="bg-slate-50 p-8 rounded-3xl border border-slate-200 space-y-6 relative shadow-sm">
          <button type="button" onClick={handleLogout} className="absolute top-8 right-8 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors">
            Logout
          </button>
          <h3 className="text-2xl font-black text-primary">{editingJob ? 'Update Job Position' : 'Create New Job Post'}</h3>
        <p className="text-sm text-slate-500">Posting as: <span className="font-bold text-slate-700">{session.user.email}</span></p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input
            type="text"
            placeholder="Job Title"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Department"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input
            type="text"
            placeholder="Location"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
          />
          <select
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option>Full-Time</option>
            <option>Part-Time</option>
            <option>Contract</option>
            <option>Remote</option>
          </select>
          <select
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.workModel}
            onChange={(e) => setFormData({ ...formData, workModel: e.target.value })}
          >
            <option>Onsite</option>
            <option>Remote</option>
            <option>Hybrid</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input
            type="text"
            placeholder="Job Code (e.g. LIB-12345)"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.jobCode}
            onChange={(e) => setFormData({ ...formData, jobCode: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <input
            type="text"
            placeholder="Salary Range (e.g. $100k - $120k)"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.salary}
            onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
          />
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Experience Level</label>
            <select
              className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white font-bold placeholder:font-normal placeholder:text-slate-300"
              value={formData.experience_level}
              onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
            >
              <option value="Junior">Entry / Junior</option>
              <option value="Mid-Level">Mid-Level</option>
              <option value="Senior">Senior</option>
              <option value="Lead">Lead / Manager</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Experience Years (e.g. 5+ Years)"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white mt-auto placeholder:font-normal placeholder:text-slate-300"
            value={formData.experience}
            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
          />
          <input
            type="text"
            placeholder="Tags (Benefits, Skills - comma separated)"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <input
            type="text"
            placeholder="Recruitment Manager"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.manager}
            onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
          />
          <input
            type="text"
            placeholder="Work Authorization (e.g. H1-B, Citizen)"
            className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.workAuth}
            onChange={(e) => setFormData({ ...formData, workAuth: e.target.value })}
          />
        </div>

        <div>
          <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block">Job Description</label>
          <textarea
            placeholder="High-level overview of the role..."
            className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary h-24 bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block">Key Responsibilities</label>
          <textarea
            placeholder="Bullet points of what they will do..."
            className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary h-32 bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.responsibilities}
            onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
          />
        </div>

        <div>
          <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block">Must-Have Skills / Requirements</label>
          <textarea
            placeholder="What qualities are needed..."
            className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary h-32 bg-white placeholder:font-normal placeholder:text-slate-300"
            value={formData.skills}
            onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
          />
        </div>

          <button type="submit" className="w-full py-4 bg-primary text-white font-black rounded-xl hover:bg-secondary transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98]">
            {editingJob ? 'Save Changes' : 'Create Job Post'}
          </button>
        </form>
      ) : activeTab === 'milestones' ? (
        <div className="space-y-8">
          <form onSubmit={handleMilestoneSubmit} className="bg-slate-50 p-8 rounded-3xl border border-slate-200 space-y-6 relative shadow-sm">
             <button type="button" onClick={handleLogout} className="absolute top-8 right-8 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors">
              Logout
            </button>
            <h3 className="text-2xl font-black text-primary">Add New Milestone</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <input
                type="text"
                placeholder="Year (e.g. 2025)"
                className="p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white font-bold placeholder:font-normal placeholder:text-slate-300"
                value={milestoneForm.year}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, year: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Milestone Title"
                className="md:col-span-3 p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white placeholder:font-normal placeholder:text-slate-300"
                value={milestoneForm.title}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                required
              />
            </div>
            <textarea
              placeholder="Milestone Description..."
              className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary h-24 bg-white"
              value={milestoneForm.description}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
              required
            />
            <button 
              type="submit" 
              disabled={isSubmittingMilestone}
              className="w-full py-4 bg-primary text-white font-black rounded-xl hover:bg-secondary transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98] disabled:opacity-50"
            >
              {isSubmittingMilestone ? 'Adding...' : 'Add Milestone'}
            </button>
          </form>

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 bg-slate-50">
              <h4 className="font-black text-primary">Timeline History</h4>
            </div>
            <div className="divide-y divide-slate-100">
              {milestones.length > 0 ? milestones.map(m => (
                <div key={m.id} className="p-6 flex items-start justify-between gap-6 group hover:bg-slate-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-secondary font-black">{m.year}</span>
                      <h5 className="font-bold text-primary">{m.title}</h5>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{m.description}</p>
                  </div>
                  <button 
                    onClick={() => handleMilestoneDelete(m.id)}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )) : (
                <div className="p-12 text-center text-slate-400 font-medium">No milestones added yet.</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 relative shadow-sm">
            <button type="button" onClick={handleLogout} className="absolute top-8 right-8 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors">
              Logout
            </button>
            <h3 className="text-2xl font-black text-primary">Manage Employee Access</h3>
            <p className="text-sm text-slate-500 mt-2">
              Send invite links to employees so they can activate their account securely.
            </p>
            <div className="grid grid-cols-1 gap-6 mt-6 max-w-xl">
              <form onSubmit={handleInviteUser} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
                <h4 className="text-lg font-black text-primary">Send Supabase Invitation</h4>
                <p className="text-xs text-slate-500">Best when email delivery is healthy and user should set password from invite link.</p>
                <input
                  type="email"
                  placeholder="employee@company.com"
                  className="w-full p-4 rounded-xl border border-slate-200 outline-none focus:border-secondary bg-white"
                  value={userInviteEmail}
                  onChange={(e) => setUserInviteEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmittingInvite}
                  className="w-full py-3 bg-primary text-white font-black rounded-xl hover:bg-secondary transition-all disabled:opacity-50"
                >
                  {isSubmittingInvite ? 'Sending Invite...' : 'Send Invite'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPortal;